import { EditableField } from "../EditableField";
import { TagChipsEditor } from "../TagChipsEditor";
import { formatRelative } from "../../lib/formatRelative";
import type { ContactData } from "../../types";

interface Props {
  data: ContactData;
  onPatch: (path: string[], value: unknown) => Promise<unknown>;
}

function Dt({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="label mb-1">{label}</dt>
      <dd className="text-sm text-ink-medium">{children}</dd>
    </div>
  );
}

export function ContactOverviewFields({ data, onPatch }: Props) {
  const personal = data.personal;
  const professional = data.professional ?? {};
  const communication = data.communication ?? {};
  const metadata = data.metadata ?? {};

  const saveField = (path: string[]) => (v: string) => onPatch(path, v);

  return (
    <div className="panel">
      <h2 className="font-heading font-semibold text-base text-ink mb-6">
        Overview
      </h2>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
        <Dt label="First name">
          <EditableField
            label="First name"
            value={personal.first_name}
            onSave={saveField(["personal", "first_name"])}
          />
        </Dt>
        <Dt label="Last name">
          <EditableField
            label="Last name"
            value={personal.last_name}
            onSave={saveField(["personal", "last_name"])}
          />
        </Dt>
        <Dt label="Email">
          <EditableField
            label="Email"
            value={personal.email}
            variant="email"
            onSave={saveField(["personal", "email"])}
          />
        </Dt>
        <Dt label="Phone">
          <EditableField
            label="Phone"
            value={personal.phone}
            variant="tel"
            onSave={saveField(["personal", "phone"])}
          />
        </Dt>
        <Dt label="LinkedIn">
          <EditableField
            label="LinkedIn"
            value={personal.linkedin_url}
            variant="url"
            onSave={saveField(["personal", "linkedin_url"])}
            renderValue={(v) =>
              v ? v.replace(/^https?:\/\//, "") : "—"
            }
          />
        </Dt>
        <Dt label="Company">
          <EditableField
            label="Company"
            value={professional.company_name}
            onSave={saveField(["professional", "company_name"])}
          />
        </Dt>
        <Dt label="Company ID">
          <EditableField
            label="Company ID"
            value={professional.company_id}
            onSave={saveField(["professional", "company_id"])}
          />
        </Dt>
        <Dt label="Job title">
          <EditableField
            label="Job title"
            value={professional.job_title}
            onSave={saveField(["professional", "job_title"])}
          />
        </Dt>
        <Dt label="Department">
          <EditableField
            label="Department"
            value={professional.department}
            onSave={saveField(["professional", "department"])}
          />
        </Dt>
        <Dt label="LinkedIn headline">
          <EditableField
            label="LinkedIn headline"
            value={professional.linkedin_headline}
            onSave={saveField(["professional", "linkedin_headline"])}
          />
        </Dt>
        <Dt label="Language">
          <EditableField
            label="Preferred language"
            value={communication.preferred_language}
            variant="select"
            options={[
              { value: "de" },
              { value: "en" },
              { value: "fr" },
            ]}
            onSave={saveField(["communication", "preferred_language"])}
          />
        </Dt>
        <Dt label="Formality">
          <EditableField
            label="Formality"
            value={communication.formality}
            variant="select"
            options={[
              { value: "formal" },
              { value: "casual" },
            ]}
            onSave={saveField(["communication", "formality"])}
          />
        </Dt>
        <Dt label="Timezone">
          <EditableField
            label="Timezone"
            value={communication.timezone}
            onSave={saveField(["communication", "timezone"])}
          />
        </Dt>
        <Dt label="Preferred channel">
          <EditableField
            label="Preferred channel"
            value={communication.preferred_channel}
            variant="select"
            options={[
              { value: "email" },
              { value: "phone" },
              { value: "linkedin" },
              { value: "whatsapp" },
            ]}
            onSave={saveField(["communication", "preferred_channel"])}
          />
        </Dt>
        <Dt label="Tags">
          <TagChipsEditor
            tags={metadata.tags ?? []}
            onChange={(next) =>
              void onPatch(["metadata", "tags"], next)
            }
          />
        </Dt>
        <Dt label="Last contact">
          <span className="text-ink-low">
            {metadata.last_contact_at
              ? formatRelative(metadata.last_contact_at)
              : "—"}
          </span>
        </Dt>
      </dl>
    </div>
  );
}
